import {
    Empty,
    Spin,
} from 'antd';
import React, {
    useEffect, useRef, useState,
} from 'react';

import {TTableHeaderStructure} from 'components/report-configuration/tabs/report-table-settings/modals/excel-header-structure-loader-modal/excel-header-structure-loader-modal.types';
import {renderColumns, ITableReportColumnProps} from 'components/table-report/table-report-columns/table-report-columns';
import {ITableReportProps} from 'components/table-report/table-report-types';
import {TEntity} from 'modules/data';
import {EErrorMessages} from 'shared/constants/messages';
import {showMessage} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {fetchDrillUpTree} from 'store/slices/table-report-slice/table-report-slice-api';
import {selectTableReportTemplateConfig} from 'store/slices/table-report-slice/table-report-slice-selectors';
import {TDrillUpParentDocument, TTableReportColumn} from 'store/slices/table-report-slice/table-report-slice-types';

import {SourceRow} from './components/source-row/source-row';

import './table-row-drillup-content.less';

interface ITableRowDrillUpContentProps {
    rowRecord: TEntity;
    headerStructure: TTableHeaderStructure | undefined;
    columns: TTableReportColumn[] | undefined;
    columnProps: ITableReportColumnProps;
    docId: ITableReportProps['docId'];
}

export const TableRowDrillUpContent = ({
    rowRecord,
    headerStructure,
    columns,
    columnProps,
    docId,
}: ITableRowDrillUpContentProps) => {
    const [sourceRowChildren, setSourceRowChildren] = useState<TDrillUpParentDocument[]>([]);
    const [sourceRowMessage, setSourceRowMessage] = useState<string | null>(null);
    const modalContentRef = useRef<HTMLDivElement>(null);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const templateConfig = useAppSelector(selectTableReportTemplateConfig);
    const {reservedColumns} = templateConfig ?? {};

    const fetchInitialData = async () => {
        const {rowIdColumnName} = reservedColumns ?? {};
        const identifierValue = rowRecord[rowIdColumnName!] as string | number;

        if (!rowIdColumnName || !identifierValue || !docId) {
            return;
        }

        setIsLoadingData(true);
        try {
            const response = await fetchDrillUpTree(
                docId,
                rowIdColumnName,
                identifierValue,
            );
            setSourceRowChildren(response.data?.parentDocuments);
            setSourceRowMessage(response.data?.message);
        } catch (error) {
            showMessage({
                message: EErrorMessages.UNEXPECTED_ERROR_OCCURRED,
                isError: true,
            });
        } finally {
            setIsLoadingData(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    return (
        <div
            className="table-report table-row-drillup-content"
            ref={modalContentRef}
        >
            {isLoadingData && (
                <div className="loading-container">
                    <Spin />
                </div>
            )}

            {!isLoadingData && (
                <>
                    {sourceRowMessage && <h2>{sourceRowMessage}</h2>}
                    {!sourceRowMessage && !sourceRowChildren?.length && <Empty />}
                    {Boolean(sourceRowChildren?.length) && (
                        <div>
                            <SourceRow
                                field={{...rowRecord, children: sourceRowChildren}}
                                sourceRowColumns={renderColumns({
                                    headerStructure,
                                    columns,
                                    columnProps,
                                    disableActions: true,
                                })}
                                columnProps={columnProps}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
